import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SmartSchedulePeriod, SmartScheduleRange } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SMART_SCHEDULE_TIMEZONE = 'Asia/Seoul';

/** Mirrors dayjs.ConfigType so schedule controls can pass their existing value. */
export type SmartScheduleDateInput = string | number | Date | Dayjs | null | undefined;

const OFFSET_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parses API dates consistently in KST. ISO strings with an explicit offset keep
 * their instant; date-only and offset-less strings are treated as KST wall time.
 */
export const toSmartScheduleKst = (value: SmartScheduleDateInput): Dayjs => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return OFFSET_SUFFIX.test(trimmed)
            ? dayjs(trimmed).tz(SMART_SCHEDULE_TIMEZONE)
            : dayjs.tz(trimmed, SMART_SCHEDULE_TIMEZONE);
    }

    return dayjs(value).tz(SMART_SCHEDULE_TIMEZONE);
};

export const getSmartScheduleDateKey = (value: SmartScheduleDateInput): string =>
    toSmartScheduleKst(value).format('YYYY-MM-DD');

export const getSmartScheduleMinutesOfDay = (value: SmartScheduleDateInput): number => {
    const date = toSmartScheduleKst(value);
    return date.hour() * 60 + date.minute() + date.second() / 60;
};

export const getSmartScheduleDurationMinutes = (
    startTime: SmartScheduleDateInput,
    endTime: SmartScheduleDateInput
): number => {
    const start = toSmartScheduleKst(startTime);
    const end = toSmartScheduleKst(endTime);
    if (!start.isValid() || !end.isValid()) return 0;
    return Math.max(0, end.diff(start, 'minute', true));
};

export const formatSmartScheduleTime = (value: SmartScheduleDateInput): string =>
    toSmartScheduleKst(value).format('HH:mm');

export const formatSmartScheduleTimeRange = (
    startTime: SmartScheduleDateInput,
    endTime: SmartScheduleDateInput
): string => `${formatSmartScheduleTime(startTime)} - ${formatSmartScheduleTime(endTime)}`;

const mondayOfWeek = (date: Dayjs): Dayjs => {
    const daysSinceMonday = (date.day() + 6) % 7;
    return date.subtract(daysSinceMonday, 'day').startOf('day');
};

export const getSmartScheduleRange = (
    period: SmartSchedulePeriod,
    selectedDay: SmartScheduleDateInput
): SmartScheduleRange => {
    const selected = toSmartScheduleKst(selectedDay);
    if (!selected.isValid()) {
        throw new Error('유효한 날짜를 선택해주세요.');
    }

    let start: Dayjs;
    let numberOfDays: number;

    if (period === 'month') {
        start = mondayOfWeek(selected.startOf('month'));
        const requiredDays = selected.endOf('month').startOf('day').diff(start, 'day') + 1;
        numberOfDays = Math.max(35, Math.ceil(requiredDays / 7) * 7);
    } else if (period === 'week') {
        start = mondayOfWeek(selected);
        numberOfDays = 7;
    } else {
        start = selected.startOf('day');
        numberOfDays = 1;
    }

    const days = Array.from({ length: numberOfDays }, (_, index) => start.add(index, 'day').format('YYYY-MM-DD'));
    const endInclusive = start.add(numberOfDays - 1, 'day');
    const endExclusive = endInclusive.add(1, 'day').startOf('day');

    return {
        period,
        startDate: start.format('YYYY-MM-DD'),
        endDate: endInclusive.format('YYYY-MM-DD'),
        startTime: start.toISOString(),
        endTime: endExclusive.toISOString(),
        days,
    };
};
